import React from 'react';

import type { PlanState } from '@aline/common';
import { Apollo } from 'src/gql/generated';

import { DrafterNodeProvider } from './SubAgentCards/DrafterNodeContext';
import { SubAgentCardList } from './SubAgentCards/SubAgentCardList';
import type { SubAgentRow } from './SubAgentCards/useSubAgentRows';
import { useSubAgentRows } from './SubAgentCards/useSubAgentRows';
import type { ChatAssistantMessage } from '../../chat-types';
import { ChatPanelMarkdown } from '../../ChatPanelMarkdown/ChatPanelMarkdown';
import { PulsingChatStatus } from '../ChatPanelAssistantMessage';
import type { ChatDocument } from '../ChatPanelAssistantMessage';
import { PinnedSkillCard } from '../pinned-skill-indicators';

/**
 * Renders a chat-harness assistant turn as a flowing sequence of stage
 * groups in start order:
 *
 *   - Planner-authored markdown (`planState.plannerText`) at the top.
 *   - For each stage, in start order:
 *       - `single` stage → the node's body rendered inline as chat
 *         prose.
 *       - `parallel` stage → its sibling nodes rendered as side-by-side
 *         cards via `SubAgentCardList`. Cards exist only to visually
 *         separate concurrent siblings; a node alone in its stage
 *         doesn't need one.
 *
 * Thread-wide artifacts (drafter / editor outputs persisted to
 * `AgentMessageDocumentJoin`) are read off the GraphQL query by the
 * `ThreadDeliverablesBar` rendered above the chat composer — no longer
 * computed in this component.
 */
export function PlanTree({
  documentInView,
  message,
  planState,
  thinkingMessage,
  threadId,
}: {
  documentInView: ChatDocument | null;
  message: ChatAssistantMessage;
  planState: PlanState;
  threadId: UUID;
  thinkingMessage?: string;
}) {
  const { rows } = useSubAgentRows(message, planState);
  const isStreaming = message.status === Apollo.AgentMessageStatus.Streaming;
  const plannerText = planState.plannerText.trim();
  const hasPlannerText = plannerText.length > 0;

  /**
   * Group rows by stage, preserving stage order (rows already come back
   * sorted by startedAt, and stages execute serially, so iterating in
   * row order produces stage groups in the correct visual order).
   */
  const stageGroups = groupRowsByStage(rows);
  const hasVisibleNodeText = rows.some(row => row.nodeText.length > 0);
  const showThinkingPulse = isStreaming && !hasPlannerText && !hasVisibleNodeText;

  return (
    <>
      {/* Pinned skills load before planning starts, so they come before the plan's status. */}
      <PinnedSkillCard message={message} threadId={threadId} />
      {showThinkingPulse && <PulsingChatStatus text={thinkingMessage ?? 'Planning…'} />}
      {hasPlannerText && (
        <ChatPanelMarkdown
          completionText={plannerText}
          documentInView={documentInView}
          message={message}
          threadId={threadId}
        />
      )}
      {stageGroups.length > 0 &&
        stageGroups.map(group => (
          <StageGroup
            key={group.stageId}
            documentInView={documentInView}
            message={message}
            rows={group.rows}
            stageType={group.stageType}
            threadId={threadId}
          />
        ))}
    </>
  );
}

interface StageGroupData {
  rows: SubAgentRow[];
  stageId: number;
  stageType: SubAgentRow['stageType'];
}

function groupRowsByStage(rows: SubAgentRow[]): StageGroupData[] {
  const byStageId = new Map<number, StageGroupData>();
  for (const row of rows) {
    const existing = byStageId.get(row.stageId);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    byStageId.set(row.stageId, {
      rows: [row],
      stageId: row.stageId,
      stageType: row.stageType,
    });
  }
  return Array.from(byStageId.values());
}

function StageGroup({
  documentInView,
  message,
  rows,
  stageType,
  threadId,
}: {
  documentInView: ChatDocument | null;
  message: ChatAssistantMessage;
  rows: SubAgentRow[];
  stageType: SubAgentRow['stageType'];
  threadId: UUID;
}) {
  if (stageType === 'parallel') {
    return (
      <SubAgentCardList
        documentInView={documentInView}
        message={message}
        rows={rows}
        threadId={threadId}
      />
    );
  }

  // Single-stage: render the lone node's body inline as part of the chat stream.
  const row = rows.at(0);
  if (!row || row.nodeText.length === 0) return null;
  return (
    <DrafterNodeProvider value={row.agent === 'drafter' ? { nodeId: row.nodeId, threadId } : null}>
      <ChatPanelMarkdown
        completionText={row.nodeText}
        documentInView={documentInView}
        message={message}
        threadId={threadId}
      />
    </DrafterNodeProvider>
  );
}
